/*
 * Zerafite: Build your bundle (sections/zf-bundle-builder.liquid)
 *
 * Vanilla JS, no dependencies, loaded with defer.
 *
 * Rules this file follows:
 * - It never sends a price or a percentage. One JSON POST to /cart/add.js carries
 *   variant id, quantity, selling_plan (only when subscribing and the variant has
 *   that plan) and the private line property _bundle.
 * - Subscription prices come from Liquid (variant.selling_plan_allocations). The
 *   only arithmetic done here is the bundle tier estimate, in integer cents, per
 *   line, and it is labelled an estimate on the page.
 * - After an add, the page prints what /cart.js returned.
 * - The add uses window.fetch with a string URL and an explicit method so the
 *   Llama Upsells fetch override sees it, and the X-Bundle-Add header so the
 *   theme's subscription widget interceptor leaves it alone.
 * - The cart drawer is refreshed the way snippets/buy-buttons.liquid does it:
 *   swap the innerHTML of .drawer__inner (the node itself stays, so the drawer's
 *   observers keep working), update #cart-icon-bubble, then drawer.open().
 *   It never publishes the theme's 'cart-update' pub/sub event.
 * - The collection filter above the grid only shows or hides cards. It never
 *   reads or changes the bundle, and it is not saved: the page opens on
 *   everything.
 */
(function () {
  'use strict';

  var STORE_KEY = 'zfBundleBuilder:v1';
  var MOBILE_QUERY = '(max-width: 989px)';
  var ADD_TIMEOUT_MS = 25000;

  /* ---------------------------------------------------------------- helpers */

  function warn() {
    try {
      var args = ['[zf-bundle]'].concat(Array.prototype.slice.call(arguments));
      console.warn.apply(console, args);
    } catch (e) { /* console unavailable */ }
  }

  function $(selector, context) {
    return (context || document).querySelector(selector);
  }

  function $all(selector, context) {
    return Array.prototype.slice.call((context || document).querySelectorAll(selector));
  }

  function fill(template, map) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return map && map[key] != null ? String(map[key]) : match;
    });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function setHidden(node, hidden) {
    if (!node) return;
    if (hidden) node.setAttribute('hidden', '');
    else node.removeAttribute('hidden');
  }

  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function withTimeout(promise, ms) {
    var timer;
    var timeout = new Promise(function (resolve, reject) {
      timer = setTimeout(function () { reject(new Error('zf-bundle request timed out')); }, ms);
    });
    return Promise.race([promise, timeout]).then(
      function (value) { clearTimeout(timer); return value; },
      function (error) { clearTimeout(timer); throw error; }
    );
  }

  function svgIcon(pathData, size) {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('width', size);
    svg.setAttribute('height', size);
    svg.setAttribute('viewBox', '0 0 16 16');
    var path = document.createElementNS(ns, 'path');
    path.setAttribute('d', pathData);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('stroke-linecap', 'round');
    svg.appendChild(path);
    return svg;
  }

  /* window.fetch is wrapped by other apps on this store. If a wrapper throws
     instead of returning a promise, turn that into a rejected promise so the
     builder's own error handling still runs. Always called as window.fetch
     with a string URL so those wrappers see the request. */
  function request(url, init) {
    try {
      return Promise.resolve(window.fetch(url, init));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /* Formats integer cents with the shop's money format (which follows the
     shopper's presentment currency). Output is plain text. */
  function makeMoney(format) {
    var decoder = document.createElement('textarea');
    function group(cents, precision, thousands, decimal) {
      var fixed = (cents / 100).toFixed(precision).split('.');
      var whole = fixed[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return whole + (fixed[1] ? decimal + fixed[1] : '');
    }
    return function (value) {
      var cents = Math.round(Number(value) || 0);
      var negative = cents < 0;
      if (negative) cents = -cents;
      var out = String(format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
        switch (key) {
          case 'amount_no_decimals': return group(cents, 0, ',', '.');
          case 'amount_with_comma_separator': return group(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return group(cents, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return group(cents, 2, "'", '.');
          case 'amount_no_decimals_with_space_separator': return group(cents, 0, ' ', ',');
          case 'amount_with_space_separator': return group(cents, 2, ' ', ',');
          case 'amount_with_period_and_space_separator': return group(cents, 2, ' ', '.');
          default: return group(cents, 2, ',', '.');
        }
      });
      decoder.innerHTML = out.replace(/<[^>]*>/g, '');
      return (negative ? '−' : '') + decoder.value;
    };
  }

  /* A cart line that an app put there as a gift (Llama Upsells marks its lines
     with _lcu / _lca_regal_upsells properties, and the gift is priced at zero).
     Gift lines never count toward bundle tiers. */
  function isGiftLine(item) {
    if (!item) return false;
    var props = item.properties || {};
    for (var key in props) {
      if (!Object.prototype.hasOwnProperty.call(props, key)) continue;
      if (key.indexOf('_lcu') === 0 || key.indexOf('_lca_regal_upsells') === 0) return true;
    }
    return item.final_line_price === 0 && item.original_line_price > 0;
  }

  /* ---------------------------------------------------------------- builder */

  function createBuilder(root) {
    var cfg;
    try {
      cfg = JSON.parse($('script[data-zfbb-config]', root).textContent);
    } catch (error) {
      warn('configuration could not be read', error);
      return null;
    }

    var S = cfg.strings || {};
    var money = makeMoney(cfg.moneyFormat);
    var rootUrl = cfg.root || '/';
    if (rootUrl.charAt(rootUrl.length - 1) !== '/') rootUrl += '/';
    var cartUrl = cfg.cartUrl || rootUrl + 'cart';
    var unique = cfg.countMode !== 'units';
    var tiers = (cfg.tiers || [])
      .filter(function (t) { return t && t.count > 0 && t.percent > 0; })
      .sort(function (a, b) { return a.count - b.count; });
    var plans = cfg.hasPlans ? (cfg.plans || []) : [];
    var products = cfg.products || [];
    var productById = Object.create(null);
    var productByHandle = Object.create(null);
    var variantById = Object.create(null);

    products.forEach(function (product) {
      productById[product.id] = product;
      productByHandle[product.handle] = product;
      (product.variants || []).forEach(function (variant) {
        variant.product = product;
        variant.plans = variant.plans || {};
        variant.max = variant.max > 0 ? variant.max : 1;
        variantById[variant.id] = variant;
      });
    });

    var state = {
      mode: plans.length && cfg.defaultMode === 'sub' ? 'sub' : 'once',
      plan: plans.length ? String(cfg.defaultPlan || plans[0].id) : null,
      sel: {},
      order: [],
      cart: null,
      lastAdd: null,
      busy: false,
      sheetOpen: false,
      sheetReturn: null,
      saidSame: false,
      filter: ''
    };

    if (state.plan && !plans.some(function (p) { return String(p.id) === state.plan; })) {
      state.plan = plans.length ? String(plans[0].id) : null;
    }

    var dom = {
      summary: $('[data-zfbb-summary]', root),
      backdrop: $('[data-zfbb-backdrop]', root),
      bar: $('[data-zfbb-bar]', root),
      live: $('[data-zfbb-live]', root),
      meters: $all('[data-zfbb-meter]', root),
      msg: $('[data-zfbb-msg]', root),
      hints: $all('[data-zfbb-hint]', root),
      cartNote: $('[data-zfbb-cart-note]', root),
      cartPanel: $('[data-zfbb-cart-panel]', root),
      barMsg: $('[data-zfbb-bar-msg]', root),
      barCount: $('[data-zfbb-bar-count]', root),
      barTotal: $('[data-zfbb-bar-total]', root),
      countLabel: $('[data-zfbb-count-label]', root),
      items: $('[data-zfbb-items]', root),
      empty: $('[data-zfbb-empty]', root),
      totalList: $('[data-zfbb-total-list]', root),
      totalSubRow: $('[data-zfbb-total-sub-row]', root),
      totalSub: $('[data-zfbb-total-sub]', root),
      totalDiscRow: $('[data-zfbb-total-disc-row]', root),
      totalDiscLabel: $('[data-zfbb-total-disc-label]', root),
      totalDisc: $('[data-zfbb-total-disc]', root),
      total: $('[data-zfbb-total]', root),
      save: $('[data-zfbb-save]', root),
      renew: $('[data-zfbb-renew]', root),
      errors: $all('[data-zfbb-error]', root),
      notices: $all('[data-zfbb-notice]', root),
      submits: $all('[data-zfbb-submit]', root),
      tierChips: $all('[data-zfbb-tier]', root),
      modeInputs: $all('input[data-zfbb-mode]', root),
      freqSelects: $all('select[data-zfbb-freq]', root),
      freqWraps: $all('[data-zfbb-freq-wrap]', root),
      sheetOpen: $('[data-zfbb-sheet-open]', root),
      sheetClose: $('[data-zfbb-sheet-close]', root),
      filter: $('[data-zfbb-filter]', root),
      filterToggle: $('[data-zfbb-filter-toggle]', root),
      filterPanel: $('[data-zfbb-filter-panel]', root),
      filterStatus: $('[data-zfbb-filter-status]', root),
      filterOptions: $all('input[data-zfbb-filter-option]', root)
    };

    if (!dom.summary || !dom.items) {
      warn('summary markup is missing');
      return null;
    }

    var cards = $all('[data-zfbb-card]', root).map(function (node) {
      var product = productById[node.getAttribute('data-product-id')];
      if (!product) return null;
      return {
        node: node,
        product: product,
        groups: (node.getAttribute('data-zfbb-collections') || '').split(' ').filter(Boolean),
        add: $('[data-zfbb-add]', node),
        stepper: $('[data-zfbb-stepper]', node),
        dec: $('[data-zfbb-dec]', node),
        inc: $('[data-zfbb-inc]', node),
        qty: $('[data-zfbb-qty]', node),
        soldout: $('[data-zfbb-soldout]', node),
        count: $('[data-zfbb-card-count]', node),
        priceLabel: $('[data-zfbb-price-label]', node),
        priceNow: $('[data-zfbb-price-now]', node),
        priceWasWrap: $('[data-zfbb-price-was-wrap]', node),
        priceWas: $('[data-zfbb-price-was]', node),
        priceNote: $('[data-zfbb-price-note]', node),
        tag: $('[data-zfbb-one-time-tag]', node),
        radios: $all('input[data-zfbb-size]', node)
      };
    }).filter(Boolean);

    var cardByProduct = {};
    cards.forEach(function (card) { cardByProduct[card.product.id] = card; });

    var rows = {};
    var mq = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;

    function isMobile() {
      return mq ? mq.matches : window.innerWidth < 990;
    }

    function alive() {
      return document.documentElement.contains(root);
    }

    /* ------------------------------------------------------------ naming */

    function nouns(count) {
      return count === 1 ? S.nounOne : S.nounMany;
    }

    function itemName(variant) {
      return variant.product.title + (variant.title ? ', ' + variant.title : '');
    }

    function planLabel(planId) {
      for (var i = 0; i < plans.length; i++) {
        if (String(plans[i].id) === String(planId)) return plans[i].label;
      }
      return '';
    }

    /* --------------------------------------------------------- selection */

    function firstAvailable(product) {
      var variants = product.variants || [];
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].available) return variants[i];
      }
      return null;
    }

    function cardVariant(card) {
      for (var i = 0; i < card.radios.length; i++) {
        if (card.radios[i].checked && variantById[card.radios[i].value]) {
          return variantById[card.radios[i].value];
        }
      }
      return firstAvailable(card.product) || card.product.variants[0];
    }

    function setQty(variantId, qty) {
      var key = String(variantId);
      var variant = variantById[key];
      if (!variant) return 0;
      qty = Math.max(0, Math.min(Math.floor(qty) || 0, variant.max));
      if (qty <= 0) {
        delete state.sel[key];
        state.order = state.order.filter(function (id) { return id !== key; });
      } else {
        state.sel[key] = qty;
        if (state.order.indexOf(key) === -1) state.order.push(key);
      }
      save();
      return qty;
    }

    /* Which selling plan a variant is sent with: the chosen plan when the
       shopper subscribes and the variant has it, otherwise none (one-time).
       Products that can only be bought on a plan always keep one. */
    function planFor(variant) {
      if (!plans.length) return null;
      var required = !!variant.product.req;
      if (state.mode !== 'sub' && !required) return null;
      if (state.plan && variant.plans[state.plan]) return state.plan;
      if (required) {
        var keys = Object.keys(variant.plans);
        return keys.length ? keys[0] : null;
      }
      return null;
    }

    function selectedLines() {
      return state.order.map(function (key) {
        var variant = variantById[key];
        var qty = state.sel[key] || 0;
        if (!variant || qty <= 0) return null;
        return { variant: variant, product: variant.product, qty: qty };
      }).filter(Boolean);
    }

    function counts() {
      var ownProducts = {};
      var ownUnits = 0;
      selectedLines().forEach(function (line) {
        ownProducts[line.product.id] = true;
        ownUnits += line.qty;
      });
      var cart = state.cart;
      var own;
      var total;
      if (unique) {
        var all = {};
        Object.keys(ownProducts).forEach(function (id) { all[id] = true; });
        if (cart) Object.keys(cart.counted).forEach(function (id) { all[id] = true; });
        own = Object.keys(ownProducts).length;
        total = Object.keys(all).length;
      } else {
        own = ownUnits;
        total = ownUnits + (cart ? cart.countedUnits : 0);
      }
      return { own: own, total: total, fromCart: total - own, ownUnits: ownUnits, ownProducts: ownProducts };
    }

    function tierFor(count) {
      var found = null;
      tiers.forEach(function (tier) { if (count >= tier.count) found = tier; });
      return found;
    }

    function nextTier(count) {
      for (var i = 0; i < tiers.length; i++) {
        if (tiers[i].count > count) return tiers[i];
      }
      return null;
    }

    /* The estimate. Integer cents, per line, subscription price first (exact,
       from Liquid), then the tier percentage on that price. */
    function estimate() {
      var c = counts();
      var tier = tierFor(c.total);
      var pct = tier ? tier.percent : 0;
      var out = { counts: c, pct: pct, list: 0, subSave: 0, disc: 0, total: 0, renew: 0, subLines: 0, lines: [] };
      selectedLines().forEach(function (line) {
        var variant = line.variant;
        var planId = planFor(variant);
        var alloc = planId ? variant.plans[planId] : null;
        var lineList = variant.price * line.qty;
        var base = alloc ? alloc[0] * line.qty : lineList;
        var off = pct ? Math.round(base * pct / 100) : 0;
        out.list += lineList;
        out.subSave += lineList - base;
        out.disc += off;
        out.total += base - off;
        if (alloc) {
          var renewBase = alloc[1] * line.qty;
          var renewOff = cfg.renewalBundle && pct ? Math.round(renewBase * pct / 100) : 0;
          out.renew += renewBase - renewOff;
          out.subLines += 1;
        }
        out.lines.push({
          variant: variant,
          product: line.product,
          qty: line.qty,
          planId: planId,
          alloc: alloc,
          list: lineList,
          base: base
        });
      });
      return out;
    }

    function meterMessage(c) {
      if (!tiers.length) return '';
      var current = tierFor(c.total);
      var next = nextTier(c.total);
      var need = next ? next.count - c.total : 0;
      if (!current && next) {
        return fill(c.total === 0 ? S.msgZero : S.msgBelow, { need: need, nouns: nouns(need), pct: next.percent });
      }
      if (current && next) {
        return fill(S.msgTier, { pct: current.percent, need: need, nouns: nouns(need), next: next.percent });
      }
      return current ? fill(S.msgTop, { pct: current.percent }) : '';
    }

    function barMessage(c) {
      if (!tiers.length) return '';
      var current = tierFor(c.total);
      var next = nextTier(c.total);
      if (next) {
        return fill(c.total === 0 ? S.barZero : S.barNeed, { need: next.count - c.total, pct: next.percent });
      }
      return current ? fill(S.barTop, { pct: current.percent }) : '';
    }

    /* ------------------------------------------------------------ storage */

    function save() {
      try {
        window.sessionStorage.setItem(STORE_KEY, JSON.stringify({
          section: cfg.sectionId,
          mode: state.mode,
          hadPlans: plans.length > 0,
          plan: state.plan,
          sel: state.order.map(function (key) { return [key, state.sel[key]]; })
        }));
      } catch (e) { /* storage unavailable */ }
    }

    function restore() {
      var saved = null;
      try { saved = JSON.parse(window.sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) { saved = null; }
      if (!saved) return;
      if (plans.length) {
        /* A mode saved in a market with no plans was forced, not chosen: ignore it. */
        if (saved.hadPlans && (saved.mode === 'sub' || saved.mode === 'once')) state.mode = saved.mode;
        if (saved.plan && plans.some(function (p) { return String(p.id) === String(saved.plan); })) {
          state.plan = String(saved.plan);
        }
      }
      (saved.sel || []).forEach(function (pair) {
        var variant = variantById[pair && pair[0]];
        if (!variant || !variant.available || !variant.product.available) return;
        setQty(variant.id, Number(pair[1]) || 0);
      });
    }

    /* --------------------------------------------------------- rendering */

    function announce(text) {
      if (!dom.live) return;
      dom.live.textContent = '';
      setTimeout(function () { dom.live.textContent = text; }, 60);
    }

    function showError(text) {
      dom.errors.forEach(function (node) {
        setText(node, text || '');
        setHidden(node, !text);
      });
    }

    function showNotice(text) {
      dom.notices.forEach(function (node) {
        setText(node, text || '');
        setHidden(node, !text);
      });
    }

    function showHint(text) {
      dom.hints.forEach(function (node) {
        setText(node, text || '');
        setHidden(node, !text);
      });
    }

    function renderCard(card) {
      var product = card.product;
      var variant = cardVariant(card);
      var qty = state.sel[String(variant.id)] || 0;
      var units = 0;
      product.variants.forEach(function (v) { units += state.sel[String(v.id)] || 0; });
      var productLive = product.available && !!firstAvailable(product);
      var buyable = productLive && variant.available;

      card.node.classList.toggle('zfbb-card--soldout', !productLive);
      card.node.classList.toggle('is-in-bundle', units > 0);

      setHidden(card.soldout, buyable);
      setHidden(card.add, !buyable || qty > 0);
      setHidden(card.stepper, !buyable || qty === 0);
      setText(card.qty, String(qty));
      if (card.inc) card.inc.setAttribute('aria-disabled', qty >= variant.max ? 'true' : 'false');

      if (card.count) {
        if (units > 0) {
          card.count.textContent = String(units);
          setHidden(card.count, false);
        } else {
          setHidden(card.count, true);
        }
      }

      card.radios.forEach(function (radio) {
        var v = variantById[radio.value];
        if (!v) return;
        radio.disabled = !v.available;
        var label = radio.parentNode ? $('[data-zfbb-pill-soldout]', radio.parentNode) : null;
        setHidden(label, v.available);
      });

      var planId = planFor(variant);
      var alloc = planId ? variant.plans[planId] : null;
      var subPrice = alloc && alloc[0] < variant.price;
      setText(card.priceLabel, subPrice ? S.priceLabelSub : S.priceLabel);
      setText(card.priceNow, money(alloc ? alloc[0] : variant.price));
      setText(card.priceWas, money(variant.price));
      setHidden(card.priceWasWrap, !subPrice);
      setHidden(card.priceNote, !subPrice);
      if (card.tag) {
        var oneTimeOnly = plans.length > 0 && state.mode === 'sub' && !planId;
        var subOnly = plans.length > 0 && state.mode !== 'sub' && !!planId;
        setText(card.tag, subOnly ? S.subscriptionOnly : S.oneTimeOnly);
        setHidden(card.tag, !(oneTimeOnly || subOnly));
      }
    }

    function buildStepper(variant) {
      var name = itemName(variant);
      var wrap = el('div', 'zfbb-stepper');
      wrap.setAttribute('role', 'group');
      wrap.setAttribute('aria-label', fill(S.qtyGroup, { title: name }));
      var dec = el('button', 'zfbb-stepper__btn');
      dec.type = 'button';
      dec.setAttribute('data-zfbb-dec', '');
      dec.setAttribute('aria-label', fill(S.decLabel, { title: name }));
      dec.appendChild(svgIcon('M3 8h10', 16));
      var value = el('span', 'zfbb-stepper__value', '1');
      var inc = el('button', 'zfbb-stepper__btn');
      inc.type = 'button';
      inc.setAttribute('data-zfbb-inc', '');
      inc.setAttribute('aria-label', fill(S.incLabel, { title: name }));
      inc.appendChild(svgIcon('M8 3v10M3 8h10', 16));
      wrap.appendChild(dec);
      wrap.appendChild(value);
      wrap.appendChild(inc);
      return { wrap: wrap, dec: dec, inc: inc, value: value };
    }

    function buildRow(variant) {
      var name = itemName(variant);
      var node = el('li', 'zfbb-item');
      node.setAttribute('data-zfbb-row', String(variant.id));

      var img = el('img', 'zfbb-item__img');
      img.alt = '';
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      if (variant.img) img.src = variant.img;

      var info = el('div', 'zfbb-item__info');
      info.appendChild(el('p', 'zfbb-item__title', variant.product.title));
      var meta = el('p', 'zfbb-item__meta');
      if (variant.title) meta.appendChild(el('span', 'zfbb-item__size', variant.title));
      var tag = el('span', 'zfbb-tag', S.oneTimeOnly);
      setHidden(tag, true);
      meta.appendChild(tag);
      info.appendChild(meta);
      var controls = el('div', 'zfbb-item__controls');
      var stepper = buildStepper(variant);
      controls.appendChild(stepper.wrap);
      info.appendChild(controls);

      var side = el('div', 'zfbb-item__side');
      var remove = el('button', 'zfbb-item__remove');
      remove.type = 'button';
      remove.setAttribute('data-zfbb-remove', '');
      remove.setAttribute('aria-label', fill(S.removeLabel, { title: name }));
      remove.appendChild(svgIcon('M4 4l8 8M12 4l-8 8', 16));
      var price = el('p', 'zfbb-item__price');
      var priceLabel = el('span', 'zfbb-sr', S.priceLabel);
      var now = el('span', 'zfbb-item__price-now');
      var wasWrap = el('span', 'zfbb-item__price-was-wrap');
      wasWrap.appendChild(el('span', 'zfbb-sr', 'Regular price'));
      var was = el('s', 'zfbb-item__price-was');
      wasWrap.appendChild(was);
      price.appendChild(priceLabel);
      price.appendChild(now);
      price.appendChild(wasWrap);
      side.appendChild(remove);
      side.appendChild(price);

      node.appendChild(img);
      node.appendChild(info);
      node.appendChild(side);

      return {
        node: node, tag: tag, stepper: stepper, remove: remove,
        priceLabel: priceLabel, now: now, was: was, wasWrap: wasWrap
      };
    }

    function renderItems(est) {
      var keep = {};
      est.lines.forEach(function (line, index) {
        var key = String(line.variant.id);
        keep[key] = true;
        var row = rows[key] || (rows[key] = buildRow(line.variant));
        var lower = line.base < line.list;
        setText(row.stepper.value, String(line.qty));
        row.stepper.inc.setAttribute('aria-disabled', line.qty >= line.variant.max ? 'true' : 'false');
        setText(row.priceLabel, line.alloc ? S.priceLabelSub : S.priceLabel);
        setText(row.now, money(line.base));
        setText(row.was, money(line.list));
        setHidden(row.wasWrap, !lower);
        var oneTimeOnly = plans.length > 0 && state.mode === 'sub' && !line.planId;
        var subOnly = plans.length > 0 && state.mode !== 'sub' && !!line.planId;
        setText(row.tag, subOnly ? S.subscriptionOnly : S.oneTimeOnly);
        setHidden(row.tag, !(oneTimeOnly || subOnly));
        if (dom.items.children[index] !== row.node) {
          dom.items.insertBefore(row.node, dom.items.children[index] || null);
        }
      });
      Object.keys(rows).forEach(function (key) {
        if (keep[key]) return;
        if (rows[key].node.parentNode) rows[key].node.parentNode.removeChild(rows[key].node);
        delete rows[key];
      });
      setHidden(dom.empty, est.lines.length > 0);
    }

    /* One entry per filled slot: the shopper's picks first, then matching
       products that are already in the cart. */
    function slotEntries() {
      var entries = [];
      var cart = state.cart;
      if (unique) {
        var seen = {};
        selectedLines().forEach(function (line) {
          var id = line.product.id;
          if (seen[id]) { seen[id].qty += line.qty; return; }
          seen[id] = { img: line.variant.img, qty: line.qty, cart: false };
          entries.push(seen[id]);
        });
        if (cart) {
          cart.countedList.forEach(function (item) {
            if (seen[item.productId]) return;
            seen[item.productId] = { img: item.img, qty: item.qty, cart: true };
            entries.push(seen[item.productId]);
          });
        }
      } else {
        selectedLines().forEach(function (line) {
          for (var i = 0; i < line.qty; i++) entries.push({ img: line.variant.img, qty: 1, cart: false });
        });
        if (cart) {
          cart.countedList.forEach(function (item) {
            for (var i = 0; i < item.qty; i++) entries.push({ img: item.img, qty: 1, cart: true });
          });
        }
      }
      return entries;
    }

    function renderMeter(meter, entries, c, message) {
      var slots = $all('[data-zfbb-slot]', meter);
      slots.forEach(function (slot, index) {
        var entry = entries[index];
        var circle = $('.zfbb-slot__circle', slot);
        var img = $('img', slot);
        var qty = $('[data-zfbb-slot-qty]', slot);
        slot.classList.toggle('is-filled', !!entry);
        slot.classList.toggle('is-cart', !!(entry && entry.cart));
        slot.classList.toggle('is-reached', !!slot.getAttribute('data-zfbb-slot-tier') && c.total >= index + 1);
        if (entry && entry.img) {
          if (!img && circle) {
            img = el('img', 'zfbb-slot__img');
            img.alt = '';
            img.width = 52;
            img.height = 52;
            circle.insertBefore(img, circle.firstChild);
          }
          if (img && img.getAttribute('src') !== entry.img) img.setAttribute('src', entry.img);
        } else if (img && img.parentNode) {
          img.parentNode.removeChild(img);
        }
        if (qty) {
          if (entry && entry.qty > 1) {
            qty.textContent = '×' + entry.qty;
            setHidden(qty, false);
          } else {
            setHidden(qty, true);
          }
        }
      });
      var more = $('[data-zfbb-slot-more]', meter);
      if (more) {
        var extra = entries.length - slots.length;
        if (extra > 0) {
          more.textContent = '+' + extra;
          setHidden(more, false);
        } else {
          setHidden(more, true);
        }
      }
      if (meter.getAttribute('role') === 'meter') {
        meter.setAttribute('aria-valuenow', String(Math.min(c.total, slots.length)));
        meter.setAttribute('aria-valuetext', message);
      }
    }

    function renderSubmit(est) {
      var isEmpty = est.lines.length === 0;
      dom.submits.forEach(function (button) {
        var label = $('[data-zfbb-submit-label]', button);
        var inPanel = button.getAttribute('data-zfbb-submit') === 'panel';
        button.setAttribute('aria-disabled', isEmpty || state.busy ? 'true' : 'false');
        button.classList.toggle('is-busy', state.busy);
        if (state.busy) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
        setText(label, state.busy ? S.ctaBusy : (isEmpty && inPanel ? S.ctaEmpty : S.cta));
      });
    }

    function render() {
      var est = estimate();
      var c = est.counts;
      var message = meterMessage(c);
      var entries = slotEntries();

      cards.forEach(function (card) { renderCard(card); });

      dom.tierChips.forEach(function (chip) {
        var reached = c.total >= Number(chip.getAttribute('data-zfbb-tier'));
        chip.classList.toggle('is-reached', reached);
        setText($('[data-zfbb-tier-state]', chip), reached ? ' (' + S.tierReached + ')' : '');
      });

      dom.meters.forEach(function (meter) { renderMeter(meter, entries, c, message); });
      setText(dom.msg, message);
      setText(dom.barMsg, barMessage(c));
      setText(dom.countLabel, fill(S.countLabel, { count: c.own, nouns: nouns(c.own) }));
      setText(dom.barCount, String(c.own));

      if (dom.cartNote) {
        if (c.fromCart > 0) {
          setText(dom.cartNote, fill(S.cartNote, { count: c.fromCart, nouns: nouns(c.fromCart) }));
          setHidden(dom.cartNote, false);
        } else {
          setHidden(dom.cartNote, true);
        }
      }

      renderItems(est);

      setText(dom.totalList, money(est.list));
      setHidden(dom.totalSubRow, !(est.subSave > 0));
      setText(dom.totalSub, money(-est.subSave));
      setHidden(dom.totalDiscRow, !(est.disc > 0));
      setText(dom.totalDiscLabel, fill(S.discLabel, { pct: est.pct }));
      setText(dom.totalDisc, money(-est.disc));
      setText(dom.total, money(est.total));
      setText(dom.barTotal, money(est.total));

      var saved = est.list - est.total;
      if (dom.save) {
        setText(dom.save, saved > 0 ? fill(S.saveLine, { amount: money(saved) }) : '');
        setHidden(dom.save, !(saved > 0));
      }
      if (dom.renew) {
        var showRenew = est.subLines > 0;
        var renewTemplate = cfg.renewalBundle && est.pct ? S.renewBundle : S.renew;
        setText(dom.renew, showRenew ? fill(renewTemplate, { amount: money(est.renew) }) : '');
        setHidden(dom.renew, !showRenew);
      }

      dom.modeInputs.forEach(function (input) { input.checked = input.value === state.mode; });
      dom.freqSelects.forEach(function (select) { if (state.plan && select.value !== state.plan) select.value = state.plan; });
      dom.freqWraps.forEach(function (wrap) { setHidden(wrap, state.mode !== 'sub'); });

      renderSubmit(est);
      return est;
    }

    /* ------------------------------------------------------------ filters */

    /* A filter only shows or hides cards. The bundle lives in state.sel, so a
       product stays in the bundle, the summary and the saved bundle while its
       card is hidden. An empty handle shows everything. */
    function applyFilter(handle, speak) {
      var name = '';
      state.filter = '';
      dom.filterOptions.forEach(function (input) {
        if (!input.value || input.value !== handle) return;
        state.filter = handle;
        var label = input.parentNode ? $('.zfbb-pill__name', input.parentNode) : null;
        name = label ? label.textContent : handle;
      });
      dom.filterOptions.forEach(function (input) { input.checked = input.value === state.filter; });
      var shown = 0;
      cards.forEach(function (card) {
        var match = !state.filter || card.groups.indexOf(state.filter) !== -1;
        setHidden(card.node, !match);
        if (match) shown += 1;
      });
      var text = fill(state.filter ? S.filterSome : S.filterAll, {
        count: shown,
        total: cards.length,
        products: cards.length === 1 ? S.productOne : S.productMany,
        name: name
      });
      setText(dom.filterStatus, text);
      if (speak) announce(text);
    }

    function setFilterOpen(open) {
      if (!dom.filterToggle || !dom.filterPanel) return;
      setHidden(dom.filterPanel, !open);
      dom.filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) revealFilterPanel();
    }

    /* On a phone the Filters button can sit just above the fixed bundle bar,
       and the panel would then open underneath it. Bring the panel into view:
       the page's scroll-padding-bottom keeps it clear of the bar, and nothing
       moves when it is already in view. Skipped where the browser does not
       take options here (it would jump the panel to the top of the window). */
    function revealFilterPanel() {
      var panel = dom.filterPanel;
      if (!panel || panel.hasAttribute('hidden') || !panel.scrollIntoView) return;
      if (!('scrollBehavior' in document.documentElement.style)) return;
      try {
        panel.scrollIntoView({ block: 'nearest' });
      } catch (error) {
        /* Not being able to scroll is not worth a warning. */
      }
    }

    /* ------------------------------------------------- cart (real numbers) */

    function analyseCart(cart) {
      var out = {
        counted: {}, countedUnits: 0, countedList: [],
        list: 0, subSave: 0, discounts: [], total: 0,
        bundleDiscount: 0, bundleLines: 0, orderDiscount: 0, itemCount: 0, signature: ''
      };
      if (!cart || !Array.isArray(cart.items)) return out;
      var index = {};
      function addDiscount(title, amount) {
        amount = Number(amount) || 0;
        if (!amount) return;
        title = title || S.cartDiscount;
        if (index[title] == null) {
          index[title] = out.discounts.length;
          out.discounts.push({ title: title, amount: 0 });
        }
        out.discounts[index[title]].amount += amount;
      }
      cart.items.forEach(function (item) {
        var variant = variantById[item.variant_id];
        var product = productById[item.product_id];
        var props = item.properties || {};
        var fromBuilder = props._bundle != null && props._bundle !== '';
        var gift = isGiftLine(item);
        var unitList = item.original_price;
        if (item.selling_plan_allocation) {
          var allocList = variant ? variant.price : item.selling_plan_allocation.compare_at_price;
          if (allocList > unitList) unitList = allocList;
        }
        out.list += unitList * item.quantity;
        out.subSave += (unitList - item.original_price) * item.quantity;
        (item.line_level_discount_allocations || []).forEach(function (allocation) {
          addDiscount(allocation.discount_application && allocation.discount_application.title, allocation.amount);
        });
        if (gift) return;
        if (fromBuilder) {
          out.bundleDiscount += Number(item.line_level_total_discount) || 0;
          out.bundleLines += 1;
        }
        /* Only variants the builder offers count: a size left out in the section
           settings (the free-gift size, say) is not covered by the discount. */
        var inScope = cfg.cartScope === 'bundle' ? fromBuilder : true;
        if (product && variant && inScope) {
          out.counted[product.id] = (out.counted[product.id] || 0) + item.quantity;
          out.countedUnits += item.quantity;
          out.countedList.push({ productId: product.id, qty: item.quantity, img: variant.img });
        }
      });
      (cart.cart_level_discount_applications || []).forEach(function (application) {
        addDiscount(application.title, application.total_allocated_amount);
        out.orderDiscount += Number(application.total_allocated_amount) || 0;
      });
      out.total = Number(cart.total_price) || 0;
      out.itemCount = Number(cart.item_count) || 0;
      out.signature = out.itemCount + ':' + out.total;
      return out;
    }

    function variantQuantities(cart) {
      var map = {};
      if (!cart || !Array.isArray(cart.items)) return map;
      cart.items.forEach(function (item) {
        if (isGiftLine(item)) return;
        var key = String(item.variant_id);
        map[key] = (map[key] || 0) + item.quantity;
      });
      return map;
    }

    function cartHasPlan(cart, variantId) {
      if (!cart || !Array.isArray(cart.items)) return true;
      return cart.items.some(function (item) {
        return String(item.variant_id) === String(variantId) && !isGiftLine(item) && !!item.selling_plan_allocation;
      });
    }

    function getCart() {
      return request(rootUrl + 'cart.js', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      }).then(function (response) {
        return response.ok ? response.json() : null;
      }).catch(function (error) {
        warn('the cart could not be read', error);
        return null;
      });
    }

    function setCart(cart) {
      if (!cart) return;
      state.cart = analyseCart(cart);
      var last = state.lastAdd;
      if (last) {
        if (Date.now() <= last.until) {
          /* Just after the add: compare what Shopify applied with the estimate. */
          var estimated = last.est.disc;
          /* Line discounts on bundle lines, plus any order-level discount. */
          var real = state.cart.bundleDiscount + state.cart.orderDiscount;
          last.mismatch = '';
          if (estimated > 0 && real === 0) last.mismatch = S.estNone;
          else if (estimated > 0 && real < estimated - last.est.lines.length) last.mismatch = S.estLower;
          last.signature = state.cart.signature;
        } else if (last.signature !== state.cart.signature) {
          last.mismatch = '';
        }
      }
    }

    function renderCartPanel() {
      var panel = dom.cartPanel;
      if (!panel) return;
      var cart = state.cart;
      if (!state.lastAdd || !cart || cart.itemCount === 0) {
        setHidden(panel, true);
        return;
      }
      while (panel.firstChild) panel.removeChild(panel.firstChild);
      panel.appendChild(el('h3', 'zfbb-cart__title', S.cartTitle));
      var list = el('dl', 'zfbb-totals zfbb-totals--cart');
      function row(label, value, total) {
        var wrap = el('div', 'zfbb-totals__row' + (total ? ' zfbb-totals__row--total' : ''));
        wrap.appendChild(el('dt', '', label));
        wrap.appendChild(el('dd', '', value));
        list.appendChild(wrap);
      }
      row(S.cartList, money(cart.list));
      if (cart.subSave > 0) row(S.cartSub, money(-cart.subSave));
      cart.discounts.forEach(function (discount) { row(discount.title, money(-discount.amount)); });
      row(S.cartTotal, money(cart.total), true);
      panel.appendChild(list);
      if (state.lastAdd.mismatch) panel.appendChild(el('p', 'zfbb-cart__note', state.lastAdd.mismatch));
      var view = el('button', 'zfbb-btn zfbb-btn--ghost', S.cartView);
      view.type = 'button';
      view.setAttribute('data-zfbb-view-cart', '');
      panel.appendChild(view);
      setHidden(panel, false);
    }

    var refreshTimer = null;
    function scheduleCartRefresh(ms) {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(function () {
        if (state.busy || !alive()) return;
        getCart().then(function (cart) {
          if (!cart || state.busy) return;
          setCart(cart);
          render();
          renderCartPanel();
        });
      }, ms == null ? 400 : ms);
    }

    /* ------------------------------------------------------ cart drawer */

    function drawerReady() {
      if (!window.customElements || !window.customElements.whenDefined) return Promise.resolve();
      return Promise.race([window.customElements.whenDefined('cart-drawer'), delay(2500)]);
    }

    function fetchSections() {
      return request(rootUrl + '?sections=cart-drawer,cart-icon-bubble&timestamp=' + Date.now(), {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      }).then(function (response) {
        return response.ok ? response.json() : null;
      }).catch(function (error) {
        warn('cart sections could not be loaded', error);
        return null;
      });
    }

    function applySections(sections) {
      var drawer = $('cart-drawer');
      var html = sections && sections['cart-drawer'];
      if (!drawer || !html) return false;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = parsed.querySelector('.drawer__inner');
      var current = drawer.querySelector('.drawer__inner');
      if (!fresh || !current) return false;
      current.innerHTML = fresh.innerHTML;
      current.classList.remove('is-empty');
      drawer.classList.remove('is-empty');
      $all('cart-drawer-items', drawer).forEach(function (node) { node.classList.remove('is-empty'); });

      var bubbleHtml = sections['cart-icon-bubble'];
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubble) {
        var bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        var freshBubble = bubbleDoc.querySelector('.shopify-section') || bubbleDoc.body;
        if (freshBubble) bubble.innerHTML = freshBubble.innerHTML;
      }
      return true;
    }

    function openDrawer(trigger) {
      var drawer = $('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return false;
      try {
        drawer.open(trigger || undefined);
        return true;
      } catch (error) {
        warn('the cart drawer could not be opened', error);
        return false;
      }
    }

    function fireCartEvents() {
      ['cart:refresh', 'cart-drawer:updated', 'cart:updated'].forEach(function (name) {
        try {
          document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
        } catch (error) {
          warn('event ' + name + ' failed', error);
        }
      });
    }

    function showCart(sections, trigger) {
      if (cfg.cartType === 'page') {
        window.location.href = cartUrl;
        return Promise.resolve();
      }
      var gifts = Promise.resolve();
      if (typeof window.__addCheckpointGifts === 'function') {
        /* The theme's checkpoint gifts are added first, so the bundled drawer
           markup would be stale: fetch it again afterwards. */
        sections = null;
        try { gifts = Promise.resolve(window.__addCheckpointGifts()).catch(function () {}); } catch (e) { /* ignore */ }
      }
      return gifts.then(drawerReady).then(function () {
        if (sections && sections['cart-drawer']) return sections;
        return fetchSections();
      }).then(function (ready) {
        var applied = false;
        try { applied = applySections(ready); } catch (error) { warn('the cart drawer could not be refreshed', error); }
        if (!applied) {
          window.location.href = cartUrl;
          return;
        }
        var opened = openDrawer(trigger);
        fireCartEvents();
        if (!opened) window.location.href = cartUrl;
      });
    }

    /* ------------------------------------------------------- add to cart */

    function buildItems(lines, withPlans) {
      return lines.map(function (line) {
        var item = {
          id: line.variant.id,
          quantity: line.qty,
          properties: { _bundle: cfg.bundleTag || 'build-your-bundle' }
        };
        if (withPlans && line.planId) item.selling_plan = Number(line.planId);
        return item;
      });
    }

    /* Round 2, order tracking. The builder leaves two notes in the browser:
       its tier table (from this section's own settings) and whether the shopper
       came here from the quiz. The script at the end of layout/theme.liquid
       reads them and writes the order details to the cart. Nothing is sent
       from here, so it cannot clash with that script or block an add. */
    function writeJourney() {
      var table = (cfg.tiers || []).map(function (tier) { return tier.count + ':' + tier.percent; }).join(',');
      try {
        localStorage.setItem('zf-bundle-tiers', table);
        if (sessionStorage.getItem('zfbb-from') === 'quiz') localStorage.setItem('zf-bundle-from', 'quiz');
      } catch (error) { /* private browsing: the order simply carries fewer details */ }
    }

    function postAdd(items) {
      /* String URL and explicit method: required for the Llama Upsells fetch
         override. Plain-object headers: required for the X-Bundle-Add check in
         snippets/subscription-widget.liquid. */
      return request(rootUrl + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-Bundle-Add': 'true'
        },
        body: JSON.stringify({
          items: items,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        })
      });
    }

    /* Sends the add, then decides what really happened by comparing the cart
       before and after. Shopify can answer 200 with an empty body (a selling
       plan that is not sold in the shopper's market), and it is not documented
       whether a failed multi-item add is all-or-nothing. */
    function tryAdd(lines, withPlans, before) {
      var out = { httpOk: false, data: {}, message: '', after: null, added: [], missing: [], withPlans: withPlans };
      return withTimeout(postAdd(buildItems(lines, withPlans)), ADD_TIMEOUT_MS)
        .then(function (response) {
          out.httpOk = response.ok;
          return response.json().catch(function () { return {}; });
        })
        .then(function (data) {
          out.data = data || {};
          if (!out.httpOk) out.message = out.data.description || out.data.message || '';
        })
        .catch(function (error) {
          warn('the add request failed', error);
        })
        .then(getCart)
        .then(function (after) {
          out.after = after;
          if (before && after) {
            var was = variantQuantities(before);
            var now = variantQuantities(after);
            lines.forEach(function (line) {
              var key = String(line.variant.id);
              var gained = (now[key] || 0) - (was[key] || 0);
              (gained >= line.qty ? out.added : out.missing).push(line);
            });
          } else {
            var returned = Array.isArray(out.data.items) ? out.data.items : (out.data.id ? [out.data] : []);
            var got = {};
            returned.forEach(function (item) { got[String(item.variant_id || item.id)] = true; });
            lines.forEach(function (line) {
              (out.httpOk && got[String(line.variant.id)] ? out.added : out.missing).push(line);
            });
          }
          return out;
        });
    }

    function markSoldOut(variant) {
      variant.available = false;
      setQty(variant.id, 0);
      var product = variant.product;
      if (!firstAvailable(product)) product.available = false;
      var card = cardByProduct[product.id];
      if (!card) return;
      var next = firstAvailable(product);
      card.radios.forEach(function (radio) {
        if (String(radio.value) === String(variant.id)) {
          radio.disabled = true;
          radio.checked = false;
        }
        if (next && String(radio.value) === String(next.id)) radio.checked = true;
      });
    }

    function reportFailure(attempt, partial) {
      var message = attempt.message;
      var soldOut = [];
      if (message && /sold out|unavailable|not available/i.test(message)) {
        attempt.missing.forEach(function (line) {
          if (message.toLowerCase().indexOf(line.product.title.toLowerCase()) !== -1) {
            soldOut.push(line);
            markSoldOut(line.variant);
          }
        });
      }
      if (soldOut.length) message = fill(S.soldOutNow, { title: soldOut[0].product.title });
      showError(message || (partial ? S.errPartial : S.errGeneric));
    }

    function nudgeEmpty() {
      if (state.sheetOpen) closeSheet(false);
      announce(S.emptyHint);
      for (var i = 0; i < cards.length; i++) {
        /* A card the filter has hidden cannot take focus. */
        if (cards[i].node.hasAttribute('hidden')) continue;
        if (cards[i].add && !cards[i].add.hasAttribute('hidden')) {
          cards[i].add.focus();
          return;
        }
      }
      /* Nothing can be added under this filter: point at the filters. */
      if (state.filter && dom.filterToggle) dom.filterToggle.focus();
    }

    function setBusy(busy) {
      state.busy = busy;
      renderSubmit(estimate());
    }

    function submit(trigger) {
      if (state.busy) return;
      var est = estimate();
      if (!est.lines.length) {
        nudgeEmpty();
        return;
      }
      var sentPlans = est.lines.some(function (line) { return !!line.planId; });
      var planDropped = false;
      var addedNow = false;
      setBusy(true);
      showError('');
      showNotice('');
      showHint('');

      getCart()
        .then(function (before) {
          return tryAdd(est.lines, true, before).then(function (attempt) {
            if (attempt.added.length === 0 && attempt.httpOk && sentPlans) {
              /* Nothing was added and Shopify did not report an error: the
                 selling plan is not available here. Add once more, one-time. */
              planDropped = true;
              return tryAdd(est.lines, false, before);
            }
            return attempt;
          });
        })
        .then(function (attempt) {
          if (attempt.after) setCart(attempt.after);

          if (attempt.added.length === 0) {
            reportFailure(attempt, false);
            return;
          }

          if (!planDropped && attempt.withPlans && attempt.after) {
            planDropped = attempt.added.some(function (line) {
              return !!line.planId && !cartHasPlan(attempt.after, line.variant.id);
            });
          }

          attempt.added.forEach(function (line) { setQty(line.variant.id, 0); });
          if (attempt.missing.length) reportFailure(attempt, true);

          addedNow = true;
          writeJourney();
          state.lastAdd = { est: est, until: Date.now() + 8000, mismatch: '', signature: '' };
          if (attempt.after) setCart(attempt.after);
          if (planDropped) {
            /* Shopify would not sell the plan to this cart: show one-time prices
               from here on, so the next add is not sent with a plan either. */
            showNotice(S.regionFallback);
            state.mode = 'once';
            save();
          }
          announce(S.success + (planDropped ? ' ' + S.regionFallback : ''));

          var returnTo = isMobile() ? dom.sheetOpen : trigger;
          if (state.sheetOpen) closeSheet(false);
          return showCart(attempt.data && attempt.data.sections, returnTo);
        })
        .catch(function (error) {
          warn('add to cart failed', error);
          showError(S.errGeneric);
        })
        .then(function () {
          state.busy = false;
          render();
          renderCartPanel();
          /* Apps (the Llama free gift) change the cart shortly after an add, so
             read it again and keep comparing it with the estimate for a moment. */
          if (addedNow && state.lastAdd) {
            state.lastAdd.until = Date.now() + 8000;
            setTimeout(function () { scheduleCartRefresh(0); }, 1500);
            setTimeout(function () { scheduleCartRefresh(0); }, 4500);
          }
        });
    }

    /* ---------------------------------------------------- mobile sheet */

    function focusables() {
      return $all('a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', dom.summary)
        .filter(function (node) {
          return !node.closest('[hidden]') && node.getClientRects().length > 0;
        });
    }

    function openSheet() {
      if (!isMobile() || state.sheetOpen) return;
      state.sheetOpen = true;
      state.sheetReturn = document.activeElement;
      dom.summary.classList.add('is-open');
      dom.summary.setAttribute('role', 'dialog');
      dom.summary.setAttribute('aria-modal', 'true');
      setHidden(dom.backdrop, false);
      if (dom.sheetOpen) dom.sheetOpen.setAttribute('aria-expanded', 'true');
      if (dom.bar) {
        /* The sheet covers the bar: take the bar out of the tab and reading order. */
        dom.bar.setAttribute('inert', '');
        dom.bar.setAttribute('aria-hidden', 'true');
      }
      document.documentElement.classList.add('zfbb-sheet-open');
      document.addEventListener('keydown', onSheetKeydown, true);
      var target = dom.sheetClose || dom.summary;
      setTimeout(function () { try { target.focus(); } catch (e) { /* ignore */ } }, 30);
    }

    function closeSheet(returnFocus) {
      if (!state.sheetOpen) return;
      state.sheetOpen = false;
      dom.summary.classList.remove('is-open');
      dom.summary.removeAttribute('role');
      dom.summary.removeAttribute('aria-modal');
      setHidden(dom.backdrop, true);
      if (dom.sheetOpen) dom.sheetOpen.setAttribute('aria-expanded', 'false');
      if (dom.bar) {
        dom.bar.removeAttribute('inert');
        dom.bar.removeAttribute('aria-hidden');
      }
      document.documentElement.classList.remove('zfbb-sheet-open');
      document.removeEventListener('keydown', onSheetKeydown, true);
      if (returnFocus) {
        var target = dom.sheetOpen || state.sheetReturn;
        try { if (target) target.focus(); } catch (e) { /* ignore */ }
      }
      state.sheetReturn = null;
    }

    function onSheetKeydown(event) {
      if (!state.sheetOpen) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        event.stopPropagation();
        closeSheet(true);
        return;
      }
      if (event.key !== 'Tab') return;
      var nodes = focusables();
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (!dom.summary.contains(document.activeElement)) {
        /* Focus slipped out of the sheet: bring it back in. */
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ------------------------------------------------------------ actions */

    function describe(prefix, est) {
      var c = est.counts;
      return [prefix, fill(S.inBundle, { count: c.own, nouns: nouns(c.own) }), meterMessage(c)]
        .filter(Boolean).join(' ');
    }

    function addFromCard(card) {
      var variant = cardVariant(card);
      if (!variant || !variant.available || !card.product.available) return;
      setQty(variant.id, 1);
      showError('');
      showHint('');
      var est = render();
      if (card.inc) card.inc.focus();
      announce(describe(fill(S.added, { title: itemName(variant) }), est));
    }

    function changeQty(variant, delta, context) {
      var key = String(variant.id);
      var current = state.sel[key] || 0;
      var beforeTier = tierFor(counts().total);
      if (delta > 0 && current >= variant.max) {
        announce(fill(S.maxReached, { title: itemName(variant) }));
        return;
      }
      var next = setQty(variant.id, current + delta);
      showError('');

      /* Keep keyboard focus on something sensible when a control disappears. */
      var focusTarget = null;
      if (next === 0) {
        if (context.row) {
          var rowNode = context.row;
          var sibling = rowNode.nextElementSibling || rowNode.previousElementSibling;
          focusTarget = sibling ? $('[data-zfbb-remove]', sibling) : dom.msg;
        } else if (context.card) {
          focusTarget = context.card.add;
        }
      }

      var est = render();
      var afterTier = tierFor(est.counts.total);
      /* More of the same product does not raise the tier when tiers count
         different products: say so on the page, and once to screen readers. */
      var sameProduct = delta > 0 && unique && beforeTier === afterTier && !!nextTier(est.counts.total);
      var hint = '';
      showHint(sameProduct ? S.msgSame : '');
      if (sameProduct && !state.saidSame) {
        hint = S.msgSame;
        state.saidSame = true;
      }

      if (focusTarget) {
        try { focusTarget.focus(); } catch (e) { /* ignore */ }
      }

      var name = itemName(variant);
      var prefix = next === 0 ? fill(S.removed, { title: name }) : fill(S.qtyNow, { title: name, qty: next });
      announce(describe(prefix, est) + (hint ? ' ' + hint : ''));
    }

    function setMode(mode) {
      if (!plans.length || (mode !== 'sub' && mode !== 'once') || mode === state.mode) return;
      state.mode = mode;
      save();
      var est = render();
      announce(fill(mode === 'sub' ? S.modeSub : S.modeOnce, { name: cfg.planName, total: money(est.total) }));
    }

    function setPlan(planId) {
      planId = String(planId);
      if (!plans.some(function (p) { return String(p.id) === planId; })) return;
      state.plan = planId;
      save();
      render();
    }

    function onClick(event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var hit = target.closest('[data-zfbb-add],[data-zfbb-inc],[data-zfbb-dec],[data-zfbb-remove],[data-zfbb-submit],[data-zfbb-sheet-open],[data-zfbb-sheet-close],[data-zfbb-backdrop],[data-zfbb-view-cart],[data-zfbb-filter-toggle]');
      if (!hit || !root.contains(hit)) return;

      if (hit.hasAttribute('data-zfbb-filter-toggle')) {
        setFilterOpen(hit.getAttribute('aria-expanded') !== 'true');
        return;
      }
      if (hit.hasAttribute('data-zfbb-sheet-open')) { openSheet(); return; }
      if (hit.hasAttribute('data-zfbb-sheet-close')) { closeSheet(true); return; }
      if (hit.hasAttribute('data-zfbb-backdrop')) { closeSheet(true); return; }
      if (hit.hasAttribute('data-zfbb-view-cart')) {
        var returnTo = isMobile() ? dom.sheetOpen : hit;
        if (state.sheetOpen) closeSheet(false);
        if (cfg.cartType === 'page' || !openDrawer(returnTo)) window.location.href = cartUrl;
        return;
      }
      if (hit.hasAttribute('data-zfbb-submit')) {
        if (state.busy) return;
        submit(hit);
        return;
      }

      /* The bundle is being sent: ignore edits until Shopify has answered. */
      if (state.busy) return;

      var cardNode = hit.closest('[data-zfbb-card]');
      var rowNode = hit.closest('[data-zfbb-row]');
      var card = cardNode ? cardByProduct[cardNode.getAttribute('data-product-id')] : null;
      var variant = null;
      if (rowNode) variant = variantById[rowNode.getAttribute('data-zfbb-row')];
      else if (card) variant = cardVariant(card);
      if (!variant) return;

      if (hit.hasAttribute('data-zfbb-add')) { if (card) addFromCard(card); return; }
      if (hit.hasAttribute('data-zfbb-inc')) { changeQty(variant, 1, { card: card, row: rowNode }); return; }
      if (hit.hasAttribute('data-zfbb-dec')) { changeQty(variant, -1, { card: card, row: rowNode }); return; }
      if (hit.hasAttribute('data-zfbb-remove')) {
        changeQty(variant, -(state.sel[String(variant.id)] || 0), { card: null, row: rowNode });
      }
    }

    function onChange(event) {
      var target = event.target;
      if (!target || !root.contains(target)) return;
      if (target.hasAttribute('data-zfbb-mode')) {
        if (target.checked) setMode(target.value);
      } else if (target.hasAttribute('data-zfbb-freq')) {
        setPlan(target.value);
      } else if (target.hasAttribute('data-zfbb-filter-option')) {
        if (target.checked) applyFilter(target.value, true);
      } else if (target.hasAttribute('data-zfbb-size')) {
        var cardNode = target.closest('[data-zfbb-card]');
        var card = cardNode ? cardByProduct[cardNode.getAttribute('data-product-id')] : null;
        if (card) renderCard(card);
      }
    }

    /* Escape inside the open filters closes them and returns to their button. */
    function onKeydown(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!dom.filter || !dom.filterPanel || dom.filterPanel.hasAttribute('hidden')) return;
      if (!dom.filter.contains(event.target)) return;
      setFilterOpen(false);
      if (dom.filterToggle) dom.filterToggle.focus();
    }

    /* ?add=<product handle> pre-adds a product (several handles may be
       separated by commas). The parameter is then removed from the address so
       a reload does not add it again. */
    /* Arriving from the quiz with ?add=: the products the shopper added there are
       picked here and then taken out of the cart, so they are in the bundle once,
       not in the cart and the bundle both. They go back into the cart, with the
       bundle discount, when the bundle is added. */
    var quizMoved = [];
    function moveQuizLines() {
      if (!quizMoved.length) return scheduleCartRefresh(0);
      getCart().then(function (cart) {
        var updates = {};
        var any = false;
        ((cart && cart.items) || []).forEach(function (item) {
          var fromBuilder = item.properties && item.properties._bundle;
          if (!fromBuilder && quizMoved.indexOf(String(item.variant_id)) !== -1) { updates[item.key] = 0; any = true; }
        });
        if (!any) return null;
        return request(rootUrl + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      }).catch(function () {}).then(function () { scheduleCartRefresh(0); });
    }

    function preAdd() {
      var params;
      try { params = new URLSearchParams(window.location.search); } catch (e) { return ''; }
      /* Round 2: remember that the shopper arrived from the quiz (its bundle button adds from=quiz). */
      try { if (params.get('from') === 'quiz') sessionStorage.setItem('zfbb-from', 'quiz'); } catch (e2) {}
      var raw = params.get('add');
      if (!raw) return '';
      var fromQuiz = params.get('from') === 'quiz';
      var messages = [];
      raw.split(',').forEach(function (part) {
        var handle = part.trim().toLowerCase();
        var product = productByHandle[handle];
        if (!product) return;
        var already = product.variants.some(function (v) { return (state.sel[String(v.id)] || 0) > 0; });
        if (already) return;
        var variant = firstAvailable(product);
        if (!product.available || !variant) {
          messages.push(fill(S.preAddSoldOut, { title: product.title }));
          return;
        }
        var card = cardByProduct[product.id];
        if (card) {
          card.radios.forEach(function (radio) { radio.checked = String(radio.value) === String(variant.id); });
        }
        setQty(variant.id, 1);
        if (fromQuiz) quizMoved.push(String(variant.id));
        messages.push(fill(S.preAdded, { title: product.title }));
      });
      try {
        params.delete('add');
        var query = params.toString();
        window.history.replaceState(window.history.state, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
      } catch (e) { /* history unavailable */ }
      return messages.join(' ');
    }

    /* --------------------------------------------------------------- init */

    restore();
    /* Show the restored size on its card. */
    state.order.forEach(function (key) {
      var variant = variantById[key];
      var card = variant ? cardByProduct[variant.product.id] : null;
      if (!card) return;
      card.radios.forEach(function (radio) {
        if (String(radio.value) === key) radio.checked = true;
      });
    });
    var preAddMessage = preAdd();

    /* Filters: a choice no card matches is left out, and so is the whole
       control when no choice is left. The page always opens on everything. */
    dom.filterOptions = dom.filterOptions.filter(function (input) {
      var used = !input.value || cards.some(function (card) { return card.groups.indexOf(input.value) !== -1; });
      if (!used) {
        input.disabled = true;
        setHidden(input.parentNode, true);
      }
      return used;
    });
    if (dom.filterOptions.length < 2) setHidden(dom.filter, true);
    applyFilter('', false);

    root.addEventListener('click', onClick);
    root.addEventListener('change', onChange);
    root.addEventListener('keydown', onKeydown);
    /* The sheet's keydown handler is bound on document while the sheet is open (openSheet). */

    if (mq) {
      var onMq = function () { if (!isMobile() && state.sheetOpen) closeSheet(false); };
      if (mq.addEventListener) mq.addEventListener('change', onMq);
      else if (mq.addListener) mq.addListener(onMq);
    }

    ['cart:updated', 'cart:refresh', 'cart-drawer:updated', 'cartQuantityUpdated'].forEach(function (name) {
      document.addEventListener(name, function () { if (alive()) scheduleCartRefresh(); });
    });
    window.addEventListener('pageshow', function (event) {
      if (event.persisted && alive()) scheduleCartRefresh(0);
    });

    /* Re-read the cart when the drawer closes, since lines can be changed or
       removed in there. */
    try {
      var drawerNode = $('cart-drawer');
      if (drawerNode && window.MutationObserver) {
        var wasActive = drawerNode.classList.contains('active');
        new MutationObserver(function () {
          var active = drawerNode.classList.contains('active');
          if (wasActive && !active && alive()) scheduleCartRefresh(300);
          wasActive = active;
        }).observe(drawerNode, { attributes: true, attributeFilter: ['class'] });
      }
    } catch (error) {
      warn('drawer observer not attached', error);
    }

    render();
    root.classList.remove('zfbb--fallback');
    root.classList.add('zfbb--ready');
    document.documentElement.classList.add('zfbb-page');

    if (preAddMessage) {
      showHint(preAddMessage);
      announce(describe(preAddMessage, estimate()));
    }

    moveQuizLines();

    return { root: root };
  }

  /* ---------------------------------------------------------------- boot */

  function boot(scope) {
    $all('[data-zfbb]', scope || document).forEach(function (root) {
      if (root.getAttribute('data-zfbb-started') === 'true') return;
      root.setAttribute('data-zfbb-started', 'true');
      try {
        createBuilder(root);
      } catch (error) {
        warn('the bundle builder could not start', error);
        root.classList.add('zfbb--fallback');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Theme editor: the section is re-rendered when a setting changes. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
